import { useEffect, useState, type ReactNode } from 'react';
import { Link, useRouterState } from '@tanstack/react-router';
import { Building2, Calculator, CheckSquare, Menu, ScrollText, UserRound, X } from 'lucide-react';
import { cn } from 'cn';

const NAV_ITEMS = [
  { to: '/biens', label: 'Biens', icon: Building2 },
  { to: '/affectation', label: 'Affectation', icon: CheckSquare },
  { to: '/ledger', label: 'Ledger', icon: ScrollText },
  { to: '/simulateur', label: 'Simulateur', icon: Calculator },
  { to: '/compte', label: 'Compte', icon: UserRound },
] as const;

export function AppShell({ children }: { children: ReactNode }) {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  return (
    <div className="flex min-h-dvh flex-col">
      <header className="grid grid-cols-[auto_1fr_auto] items-center gap-3 border-b border-border bg-card px-4 py-3 print:hidden">
        <button
          type="button"
          onClick={() => setMenuOpen(true)}
          aria-label="Ouvrir le menu"
          aria-expanded={menuOpen}
          className="rounded-md p-2 text-foreground hover:bg-accent hover:text-accent-foreground"
        >
          <Menu className="size-6" />
        </button>
        <div className="flex items-center justify-center gap-2.5">
          <img src="/logo.webp" alt="" className="size-8 rounded-full" />
          <p className="font-serif text-lg font-bold text-primary">Evasion-Soleil</p>
        </div>
        <div aria-hidden="true" className="size-6 p-2" />
      </header>

      {menuOpen && (
        <div className="fixed inset-0 z-20 print:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMenuOpen(false)} aria-hidden="true" />
          <aside className="absolute inset-y-0 left-0 flex w-72 max-w-[85vw] flex-col bg-card p-4 shadow-xl">
            <div className="mb-4 flex items-center justify-between px-2">
              <p className="font-serif text-lg font-bold text-primary">Menu</p>
              <button
                type="button"
                onClick={() => setMenuOpen(false)}
                aria-label="Fermer le menu"
                className="rounded-md p-2 text-muted-foreground hover:bg-accent hover:text-accent-foreground"
              >
                <X className="size-5" />
              </button>
            </div>
            <nav aria-label="Navigation principale" className="flex flex-col gap-1">
              {NAV_ITEMS.map((item) => (
                <NavLink key={item.to} item={item} active={pathname.startsWith(item.to)} />
              ))}
            </nav>
          </aside>
        </div>
      )}

      <main className="flex-1 overflow-y-auto">
        <div className="w-full p-4 md:p-8">{children}</div>
      </main>
    </div>
  );
}

function NavLink({ item, active }: { item: (typeof NAV_ITEMS)[number]; active: boolean }) {
  const Icon = item.icon;
  return (
    <Link
      to={item.to}
      aria-current={active ? 'page' : undefined}
      className={cn(
        'flex items-center gap-2 rounded-md px-3 py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground',
        active && 'bg-accent font-semibold text-accent-foreground',
      )}
    >
      <Icon className="size-5" />
      {item.label}
    </Link>
  );
}
